import React from "react";
import { FlaskConical, Cloud, Building2, Scale, Landmark, Coins, Users, Rocket } from "lucide-react";
import { motion } from "framer-motion";

const FACILITIES = [
  { label: "State-of-the-art lab facilities", icon: FlaskConical },
  { label: "Computing and cloud infrastructure", icon: Cloud },
  { label: "Co-working spaces at IIT Ropar & Bangalore", icon: Building2 },
  { label: "Support in legal and financial compliances", icon: Scale },
  { label: "Access to government and PSU funding schemes", icon: Landmark },
  { label: "Access to Angel and VC funding networks", icon: Coins },
  { label: "Mentorship from 180+ faculty & industry experts", icon: Users },
  { label: "Cohorts, incubation and acceleration programs", icon: Rocket },
];

/* ===== Animation Variants ===== */
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.5 } 
  },
};

export function FacilitiesList({ className, title }) {
  return (
    <section
      className={`relative w-full py-20 px-6 ${className ?? ""}`}
      aria-labelledby="facilities-heading"
    >
      {/* Background Texture (Optional, matches other components) */}
      <div className="absolute inset-0 bg-[radial-gradient(#e2e8f0_1px,transparent_1px)] [background-size:20px_20px] opacity-40 pointer-events-none"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* Header */}
        <div className="text-center mb-16">
          <h2 id="facilities-heading" className="text-3xl md:text-4xl font-bold text-slate-900">
            {title ?? (
              <>
                <span className="text-slate-900">Additional</span>{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">
                  Capabilities
                </span>
              </>
            )}
          </h2>
          <div className="w-20 h-1 bg-blue-500 mx-auto mt-4 rounded-full opacity-20"></div>
        </div>

        {/* Grid */}
        <motion.ul 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {FACILITIES.map(({ label, icon: Icon }, i) => (
            <motion.li key={i} variants={itemVariants} className="h-full">
              <div
                className="group h-full flex flex-col items-start gap-5 p-6 bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-xl hover:-translate-y-1 hover:border-blue-200 transition-all duration-300 relative overflow-hidden"
              >
                {/* Top Border Highlight */}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-cyan-400 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>

                {/* Icon */}
                <div className="w-12 h-12 rounded-xl bg-slate-50 text-blue-600 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300 shadow-sm group-hover:shadow-blue-500/30">
                  <Icon className="w-6 h-6" strokeWidth={1.5} />
                </div>
                
                {/* Text */}
                <p className="text-slate-600 font-medium leading-relaxed group-hover:text-slate-900 transition-colors">
                  {label}
                </p>

                {/* Subtle Background Icon (Decoration) */}
                <Icon className="absolute -bottom-4 -right-4 w-24 h-24 text-slate-100 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 transform rotate-12" />
              </div>
            </motion.li>
          ))}
        </motion.ul>

      </div>
    </section>
  );
}